import { Box, IconButton, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ScopeBar from "./ScopeBar";
import { useScope } from "../context/ScopeContext";
import { PREMISE_SCOPED_PREFIXES } from "../nav/products";

/**
 * Full-height workspaces (the premise editor) need to know how much room the
 * header takes. Publishing it as a CSS variable keeps them exact even when the
 * bar wraps to two rows on a narrow window.
 */
export const HEADER_HEIGHT_VAR = "--foyer-app-header-h";

/**
 * Title for the routes that mount no `PageHeader` of their own - the premise
 * editor and its tab panels. The premise directory is already loaded for the
 * Location selector, so the real name costs nothing.
 */

function useFallbackTitle(pathname: string): string {
  const { t } = useTranslation("shell");
  const { premises, scope } = useScope();
  const named = (slug: string) =>
    premises.find((p) => p.premiseId === slug)?.premiseName ??
    t("header.premiseFallback");
  const slug = /^\/premises\/([^/]+)/.exec(pathname)?.[1];
  if (slug) return named(slug);
  // Inside a product area the page belongs to the scoped location - name it,
  // so you always know which one you are editing.
  if (
    PREMISE_SCOPED_PREFIXES.some((p) => pathname.startsWith(p)) &&
    scope.premiseId
  ) {
    return named(scope.premiseId);
  }
  if (pathname.startsWith("/templates/")) {
    return t("header.templateTitle");
  }
  return t("header.appName");
}

/**
 * The bar that sits above every page: the page's own identity on the left
 * (portalled in by `PageHeader`, so it stays a per-page concern) and the
 * app-wide scope selectors on the right.
 */
export default function AppHeader({
  slotRef,
  claimed,
  onOpenNav,
}: {
  /** Ref callback for the title slot PageHeader portals into. */
  slotRef: (el: HTMLDivElement | null) => void;
  /** Whether a PageHeader currently owns the slot. */
  claimed: boolean;
  /**
   * Opens the nav. Passed ONLY on a handset, where the drawer is temporary and
   * would otherwise have no way in - on desktop it is permanent and needs no
   * button. Absent means "render nothing", so this bar has no idea what a
   * breakpoint is.
   */
  onOpenNav?: () => void;
}) {
  const { t } = useTranslation("shell");
  const location = useLocation();
  const title = useFallbackTitle(location.pathname);
  const [el, setEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        HEADER_HEIGHT_VAR,
        `${el.offsetHeight}px`
      );
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty(HEADER_HEIGHT_VAR);
    };
  }, [el]);

  return (
    <Box
      component="header"
      ref={setEl}
      data-testid="app-header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (t) => t.zIndex.appBar,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 2,
        rowGap: 1.5,
        px: { xs: 2.25, md: 3 },
        py: { xs: 1.75, md: 2 },
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        backdropFilter: "saturate(180%) blur(8px)",
      }}
    >
      {onOpenNav && (
        <IconButton
          onClick={onOpenNav}
          edge="start"
          aria-label={t("header.openNavigation")}
          data-testid="app-header-nav"
          // Flex-none so it never gets squeezed by the title beside it, which
          // is exactly what a 320px-basis sibling would otherwise do to it.
          sx={{ flex: "none", ml: -0.5 }}
        >
          <MenuIcon />
        </IconButton>
      )}
      <Box sx={{ minWidth: 0, flex: "1 1 320px" }}>
        {/* Portal target - kept free of React children so the two never race. */}
        <Box ref={slotRef} />
        {!claimed && (
          <Typography
            component="h1"
            sx={{
              typography: "brand",
              fontSize: { xs: "1.35rem", md: "1.5rem" },
              lineHeight: 1.15,
            }}
          >
            {title}
          </Typography>
        )}
      </Box>
      <ScopeBar />
    </Box>
  );
}
